import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import StatisticsTable from '../../components/StatisticsTable';
import { userTransitionsSelector, invertedTransitionsJsonSelector, transitionsJsonDatesSelector } from '../../reducers/transitions/transitionsReducer';
import { userPossibleTransitionsSelector, invertedRightsJsonSelector } from '../../reducers/rights/rightsReducer';
import { STATUSES, RIGHTS } from '../../constants';
import { dateFilter } from '../../utils/common';

const StatisticsByProject = ({ multiselectedUsers, projectsForStatistics, selectedDate: {startDate, endDate} }) => {
  const [ t ] = useTranslation();

  const { users: invertedUsers, projects: invertedProjects, states: invertedStates } = useSelector(invertedTransitionsJsonSelector);
  const { rights: invertedRights } = useSelector(invertedRightsJsonSelector);
  const userTransitions = useSelector(userTransitionsSelector);
  const userPossibleTransitions = useSelector(userPossibleTransitionsSelector);
  const datesJson = useSelector(transitionsJsonDatesSelector);

  const selectedPathsObject = useMemo(() => {
    const paths = {};
    projectsForStatistics.forEach(({ path }) => {paths[path] = invertedProjects[path]});
    
    return paths;
  }, [projectsForStatistics, invertedProjects]);

  const selectedUsersObject = useMemo(() => {
    const users = {};
    multiselectedUsers.forEach(user => {users[user] = invertedUsers[user]});
    
    return users;
  }, [multiselectedUsers ,invertedUsers]);

  const statesAndRightsCodes = useMemo(() => ({
    // backend returns only statuses for which it has transition
    // backend returns all rights, so no additional check needed
    [invertedStates[STATUSES.COMPLETED] || '999991']: invertedRights[RIGHTS.WRITE],
    [invertedStates[STATUSES.REVIEWED] || '999992']: invertedRights[RIGHTS.REVIEW],
    [invertedStates[STATUSES.LOCKED] || '999993']: invertedRights[RIGHTS.LOCK]
  }), [invertedStates, invertedRights]);

const statesToCount = useMemo(() => Object.keys(statesAndRightsCodes),
  [statesAndRightsCodes]
);

  /**
   * function projectStatistics
   * STATEMENT: works if user codes and project codes are the same 
   * in rights.json and transitions.json
   * statement TRUE because of backend implementation 
   * @param {object} project
   * @return {user1: {stateCode1: {transitions: {stateCount1, ...}, possibleTransitions: {rightStepCount}}, user2: ...}
   */
  const projectStatistics = useCallback(project => {
    if (!project) return {};
    const projectCode = selectedPathsObject[project.path];
    const statistics = {};
    multiselectedUsers.forEach(nameUser => {
      const userCode = invertedUsers[nameUser];
      const transitionCount = userTransitions[userCode] || {};
      const possibleTransitionsCount = userPossibleTransitions[userCode] || {};  
      statistics[userCode] = {
        transitions: transitionCount[projectCode] || {},
        possibleTransitions: possibleTransitionsCount[projectCode] || {}
      };
    });

    return statistics;
  }, [
    userTransitions,
    userPossibleTransitions,
    invertedUsers,
    multiselectedUsers,
    selectedPathsObject
  ]);
  
  const generateTableRows = useCallback(project => {
    const statistics = projectStatistics(project);
    return multiselectedUsers.map(nameUser => {
      const userCode = selectedUsersObject[nameUser];
      return [nameUser, ...statesToCount.map(state => {
        const rightCode = statesAndRightsCodes[state];
        // if there is no user in statistics, return 0/0 0%
        if (!statistics[userCode]) return <span>0/0<span className="align-right">0%</span></span>;

        // convert num date to date object for filtering
        const userTransitionDates = statistics[userCode].transitions[state] ?
          statistics[userCode].transitions[state].map(dateKey => new Date(datesJson[dateKey])) :
          [];
        const doneTransitions = dateFilter(userTransitionDates, startDate, endDate);
        const totalTransitions = statistics[userCode].possibleTransitions[rightCode] || 0;
        const allTransitions = project.sidekick.total_steps;

        const progress = ((allTransitions > 0) ? (doneTransitions / allTransitions) * 100 : 0).toFixed(1);

        return <span>{doneTransitions}/{totalTransitions}<span className="align-right">{progress}%</span></span>;
      })];
    })
  }, [
    startDate,
    endDate,
    multiselectedUsers,
    projectStatistics,
    datesJson,
    statesToCount,
    selectedUsersObject,
    statesAndRightsCodes
  ]);


  const projectStatisticsHeader = useMemo(() => [
    t('common.user'),
    t('statisticsTable.headers.completed'),
    t('statisticsTable.headers.reviewed'),
    t('statisticsTable.headers.locked')
  ], [t]);

  return (
    <div className="current-steps-statistics-table">
        {projectsForStatistics.map(project => {
          return (
            <div key={project.path}>
              <h4>{t('common.project')}: {project.name}</h4>
              <StatisticsTable
                tableHeaders={projectStatisticsHeader}
                tableData={generateTableRows(project)}
              />
            </div>
          );
        }
      )}
    </div>
  );
};

StatisticsByProject.propTypes = {
  multiselectedUsers: PropTypes.arrayOf(PropTypes.string),
  projectsForStatistics: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  })
};

export default StatisticsByProject;
